import { useCallback, useEffect, useRef } from "react";

type Timer = ReturnType<typeof setTimeout>;
type Func = (...args: any[]) => void;

export const useDebounce = <F extends Func>(
	fn: F,
	ms: number
): [Func, () => void] => {
	const timeoutRef = useRef<Timer>();
	const funcRef = useRef<F>(fn);

	useEffect(() => {
		funcRef.current = fn;
	}, [fn]);

	const cancel = useCallback(() => {
		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current);
			timeoutRef.current = undefined;
		}
	}, []);

	useEffect(() => {
		return () => cancel();
	}, [cancel]);

	const debouncedFunc = useCallback(
		(...args: any[]) => {
			cancel();
			timeoutRef.current = setTimeout(() => {
				funcRef.current(...args);
			}, ms);
		},
		[ms, cancel]
	);

	return [debouncedFunc, cancel];
};
